import objectPath from 'object-path';
import {
  observable,
  action,
  autorun,
  extendObservable,
  isComputedProp,
  isObservable,
  getAtom,
  runInAction,
  untracked,
  reaction,
} from 'mobx';
import { client } from 'core/client';
import { unObserve } from 'core/utils';

import {
  handleGoErrors,
  handlePythonErrors,
  logBadRequestQuery,
} from './errors';

export class Variables {
  disposers = [];
  props = [];

  constructor(obj = {}) {
    Object.keys(obj).forEach(key => {
      this.props.push(key);
      if (typeof obj[key] === 'function') {
        Object.defineProperty(obj, key, { get: obj[key] });
      }
    });
    extendObservable(this, obj);
  }

  @action extend = newVars => {
    const newObj = {};
    this.props.forEach(prop => {
      if (isComputedProp(this, prop)) {
        newObj[prop] = getAtom(this, prop).derivation;
      }
    });
    newVars.props.forEach(prop => {
      if (isComputedProp(newVars, prop)) {
        newObj[prop] = getAtom(newVars, prop).derivation;
      }
    });
    return new Variables(newObj);
  };

  @action dispose = () => {
    this.disposers.forEach(disposer => disposer());
  };

  @action onChange = (varNames, handler, { fireImmediately = true } = {}) => {
    this.disposers.push(
      reaction(
        () => {
          const newVars = {};
          varNames.forEach(varName => {
            const varSegs = varName.split('.');
            let target = this;
            varSegs.forEach(seg => {
              newVars[seg] = target[seg];
              target = target[seg];
            });
          });
          return newVars;
        },
        newVars => {
          handler(newVars);
        },
        { name: `On vars changed ${varNames}`, fireImmediately },
      ),
    );
  };
}

// Similar to objectPath.get but works on observables and pathSegs is already a segments array
export const resultFromPathSegments = (res, pathSegs) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const path of pathSegs) {
    res = res[path];
    // Another option would be using res == null (two equals),
    // but could be less explicit and prone to be modified back to === in the future.
    if ([null, undefined].includes(res)) return null;
  }
  return res;
};

export class Processor {
  @observable result = null;

  @observable loaded = false;

  disposer;

  constructor(
    query,
    processorFunc,
    callback,
    path = null,
    ignoreInitial = false,
  ) {
    // query.result is already the sub path of the raw result (using query.constructor.conf.resultPath)
    this.query = query;
    const pathSegments = ['result'];
    if (path !== null) {
      pathSegments.push(...path.split('.'));
    }
    this.initial = true;
    this.disposer = autorun(
      () => {
        const resultPart = resultFromPathSegments(query, pathSegments);
        if (this.initial && ignoreInitial) {
          this.initial = false;
          return;
        }
        this.initial = false;
        const result = processorFunc(
          resultPart,
          untracked(() => this.result),
        );
        runInAction(() => {
          this.result = result;
          if (callback) {
            callback(this.result, query);
          }
        });
      },
      { name: `Processor: ${query.constructor.name}` },
    );
  }

  dispose() {
    this.disposer();
  }
}

// FIXME - resultPath is a required parameter, check for it on constructor
export class Query {
  @observable loading = true;
  @observable updating = false;
  @observable result = null;
  @observable userErrors = null;
  @observable sysError = null;
  @observable notFound = false;
  processors = observable.map();
  onSysErr = null;
  disposers = [];
  queryStack = undefined;

  constructor(
    appManager,
    {
      queryKey = null,
      onUserErr = null,
      onSysErr = error => {
        console.error(error);
      },
      fetchPolicy = 'cache-and-network',
      pollInterval = undefined,
      variables = null,
      headers = null,
      abortOnRerequest = false,
    } = {},
  ) {
    this.setOnSysError(onSysErr);
    this.setOnUserError(onUserErr);
    this.errored = false;
    this.appManager = appManager;
    this.queryKey = queryKey || this.appManager.queries.size;
    this.variables = variables || this.appManager.variables;
    this.fetchPolicy = fetchPolicy;
    this.pollInterval = pollInterval;
    this.abortOnRerequest = abortOnRerequest;
    this.headers = headers;
    this.createQuery();
    appManager.queries.set(this.queryKey, this);
  }

  createQuery = () => {
    let context = {};
    if (this.abortOnRerequest) {
      context = {
        abortOnRerequest: true,
        queryDeduplication: false,
      };
    }
    if (this.headers) {
      context.headers = this.headers;
    }
    this.observableQuery = client.watchQuery({
      query: this.constructor.conf.GQL,
      variables: isObservable(this.variables)
        ? unObserve(this.variables, this.constructor.conf.varNames)
        : this.variables,
      fetchPolicy: this.fetchPolicy,
      pollInterval: this.pollInterval,
      context,
    });
  };

  query = () => {
    if (ENV_NAME !== 'production') {
      this.queryStack = new Error().stack;
    }

    this.subscription = this.observableQuery.subscribe({
      next: this.Update,
      error: this.SysError,
      // complete: () => {},
    });
  };

  cancel = () => {
    this.subscription.unsubscribe();
    this.errored = true;
  };

  process(
    processor,
    callback,
    { subPath = null, processorKey = null, ignoreInitial = false } = {},
  ) {
    if (processorKey === null) {
      processorKey = this.processors.size;
    }
    this.processors.set(
      processorKey,
      new Processor(this, processor, callback, subPath, ignoreInitial),
    );
  }

  @action Update = result => {
    this.queryStack = null;
    this.sysError = null;
    this.notFound = false;
    const { resultPath } = this.constructor.conf;
    if (resultPath && !objectPath.has(result.data, resultPath)) {
      console.error(
        `Trying to get resultPath '${resultPath}' on Query ${this.constructor.name} which does not exist on the result`,
      );
    }

    this.result = objectPath.get(result.data, resultPath, null);
    if (!result.loading || result.data !== undefined) {
      this.loading = false;
    }
    // We have a cache hit - we don't want to go into loading mode.
    // However, we may want to indicate that we are updating
    if (result.data !== undefined && result.loading) {
      this.updating = true;
    } else {
      this.updating = false;
    }

    if (this.constructor.conf.errorPath) {
      this.userErrors = objectPath.get(
        result.data,
        this.constructor.conf.errorPath,
        [],
      );
      handlePythonErrors(this);
    }
  };

  @action SysError = err => {
    const [sysErr, userErrs] = handleGoErrors(this, err);

    this.loading = this.observableQuery.getCurrentResult().loading;
    // Hack around bug in apollo which doesn't allow refetching after an error is thrown
    // https://github.com/apollographql/apollo-client/issues/8833
    this.errored = true;

    if (userErrs.length !== 0) {
      // If there are in fact user errors (graphqlErrors which are expected and therefore not system errors)
      this.userErrors = userErrs;
      if (this.onUserErr) {
        this.onUserErr(userErrs);
      }
    } else if (sysErr) {
      if (ENV_NAME !== 'production') {
        console.warn(this);
        console.warn(this.queryStack);
      }
      console.error(err);
      this.sysError = err;

      if (this.onSysErr) {
        this.onSysErr(err);
      }

      // BAD_REQUEST
      if (err.networkError && err.networkError.statusCode === 400) {
        logBadRequestQuery(err, this);
      }
    }
  };

  setOnSysError(callback) {
    this.onSysErr = callback;
  }

  setOnUserError(callback) {
    this.onUserErr = callback;
  }

  refetchQuery = (onComplete = () => true) => {
    this.loading = true;
    this.notFound = false;
    // Hack around bug in apollo which doesn't allow refetching after and error is thrown
    if (this.errored) {
      this.errored = false;
      this.disposer();
      this.subscription = null;
      this.createQuery();
      this.query();
      return;
    }
    const refetchPromise = this.observableQuery.refetch(
      isObservable(this.variables)
        ? unObserve(this.variables, this.constructor.conf.varNames)
        : this.variables,
    );
    refetchPromise.then(result => {
      // When you refetch queries with different variables and they return the same result...
      // The observableQuery next/error/complete callback are never called.
      // We use this promise to ensure loading is set to false after the refetch completes.
      this.loading = this.observableQuery.getCurrentResult().loading;
      onComplete(result);
      return result;
    });
  };

  disposer() {
    // unnecssary all reactions are on processors
    // eslint-disable-next-line no-restricted-syntax
    for (const dispose of this.disposers) {
      dispose();
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const processor of this.processors.values()) {
      processor.dispose();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
